<template>
  <b-container fluid>
    <h1 class="m-3">
      {{ title }}
    </h1>
    <commonActions :data-ready="dataReadyTables" :multiple="true" :filename-prefix="title"
      @commonFiltersUpdated="commonFiltersUpdated($event)" @clickDownloadPdf="downloadPdf($event)"
      @clickDownloadExcel="downloadExcel($event)" @clickDownloadCsv="downloadCsv($event)" />
    <table-renderer :data-table="dataTable1" :dimension-fields="dimensionColumnsTable1"
      :measure-fields="measureColumnsTable1" :group-headers="groupHeadersTable1"
      :default-sorted-column="defaultSortedColumnTable1" :totals-data="totalsDataTable1" :data-ready="dataReadyTable1"
      :custom-table-class="customTableClass" ref="tableRenderers1" />
    <table-renderer :data-table="dataTable2" :dimension-fields="dimensionColumnsTable2"
      :measure-fields="measureColumnsTable2" :group-headers="groupHeadersTable2"
      :default-sorted-column="defaultSortedColumnTable2" :totals-data="totalsDataTable2" :data-ready="dataReadyTable2"
      :custom-table-class="customTableClass" ref="tableRenderers2" />
  </b-container>
</template>

<script>
import lodash from "lodash";
import Tools from "@/utils/tools";
import paths from "@/router/pathConstants";
import TableTools from "@/utils/tableTools";
import TableRenderer from "@/components/TableRenderer.vue";
import commonActions from "@/components/pages/commonActions";
import mesureEnum from "@/model/mesureEnum";
import dimensionEnum from "@/model/dimensionEnum";
import {
  columnTypeEnum,
  groupKeyEnum,
  calculatedColumnsEnum,
} from "@/model/columnsEnums";
import { snackProductTypeEnum } from "@/model/businessEnum";
import { OperationsHourly } from "@/router/routeNames";
import abortMixin from "@/mixins/abortMixin";

export default {
  name: OperationsHourly,
  mixins: [abortMixin],
  components: {
    TableRenderer,
    commonActions,
  },
  data() {
    return {
      /** Tableau de filtres tels qu'attendu par cubeJs.
       * Ex :
       *    [
       *      {
       *        dimension: "Site.label",
       *        operator: "equals",
       *        values: ["Site 1"]
       *      },
       *      {
       *        dimension: "BusinessDate.id",
       *        operator: "inDateRange",
       *        values: ["2020-09-23", "2020-09-26"]
       *      },
       */
      commonFilters: [],
      timeDimension: null,
      dataReadyTable1: false,
      dataTable1: [],
      dimensionColumnsTable1: [],
      measureColumnsTable1: [],
      groupHeadersTable1: [],
      defaultSortedColumnTable1: "",
      totalsDataTable1: null,
      dataReadyTable2: false,
      dataTable2: [],
      dimensionColumnsTable2: [],
      measureColumnsTable2: [],
      groupHeadersTable2: [],
      defaultSortedColumnTable2: "",
      totalsDataTable2: null,
    };
  },
  computed: {
    dataReadyTables() {
      return this.dataReadyTable1 && this.dataReadyTable2;
    },
    title() {
      return this.$t("title.operations_hourly");
    },
    customTableClass() {
      return "table-width-medium";
    },
  },
  mounted() {
    this.initDisplayTable1();
    this.initDisplayTable2();
  },
  methods: {
    commonFiltersUpdated(value) {
      this.commonFilters = value.filters;
      this.timeDimension = value.timeDimension || null;
      this.updateTables();
    },
    updateTables() {
      var dimensionsCommon = [dimensionEnum.TIME_HOUR];
      this.updateTable1(this.commonFilters, dimensionsCommon);
      this.updateTable2(this.commonFilters, dimensionsCommon);
    },
    async updateTable1(filters, dimensions) {
      try {
        const { signal } = this.resetAbortController("table1");
        this.dataReadyTable1 = false;

        var queries = [];

        var birthdayFilter = TableTools.Helper.getFilterPurchaseHistoryNotBirthday();
        var purchaseHistoryFilters = [...filters];
        purchaseHistoryFilters.push(birthdayFilter);
        var newQuery = {
          measures: [
            mesureEnum.PURCHASEHISTORY_AMOUNT,
            mesureEnum.PURCHASEHISTORY_TOKEN,
            mesureEnum.PURCHASEHISTORY_BONUSTOKEN,
            mesureEnum.PURCHASEHISTORY_COUNTSOLDCARDS,
          ],
          timeDimensions: this.timeDimension
            ? [{ ...this.timeDimension, dimension: dimensionEnum.PURCHASEHISTORY_DATETIME }]
            : [],
          dimensions: dimensions,
          filters: purchaseHistoryFilters,
        };
        queries.push({ query: newQuery });

        newQuery = {
          measures: [mesureEnum.ADJUSTMENTHISTORY_TOKENCREDITED],
          timeDimensions: this.timeDimension ? [this.timeDimension] : [],
          dimensions: dimensions,
          filters: filters,
        };
        queries.push({ query: newQuery });

        newQuery = {
          measures: [mesureEnum.STAFFGAMEHISTORY_TOKENCREDITED],
          timeDimensions: this.timeDimension ? [this.timeDimension] : [],
          dimensions: dimensions,
          filters: filters,
        };
        queries.push({ query: newQuery });

        newQuery = {
          measures: [mesureEnum.SNACKHISTORY_AMOUNT_CB_TTC],
          timeDimensions: this.timeDimension ? [this.timeDimension] : [],
          dimensions: dimensions,
          filters: filters,
        };
        queries.push({ query: newQuery });

        var resultSets = await TableTools.Helper.doMultipleRequest(queries, { signal });
        this.dataTable1 = TableTools.Helper.mergeResultsByCommonDimensions(
          resultSets,
          dimensions
        );
        this.addCalculatedDataToDataTable1();
        this.dataTable1 = this.completeAndCleanHours(
          this.dataTable1,
          this.measureColumnsTable1.map((col) => col.key)
        );

        this.totalsDataTable1 = TableTools.Helper.calculateTotalsData(
          this.dataTable1,
          this.measureColumnsTable1.map((col) => col.key)
        );

        this.dataReadyTable1 = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        Tools.Alerts.Error(this.$swal, error);
      }
    },
    /** Initialise tout ce qui concerne l'affichage : quelles colonnes doivent réellement être affichées, dans quel ordre, dans quel groupe...
     * Attention : séparé ici car pour ce tableau tout est "figé", pas besoin de le refaire en fonction des valeurs choisies dans les filtres, mais ça n'est pas toujours le cas
     */
    initDisplayTable1() {
      this.dimensionColumnsTable1 = TableTools.Helper.constructColumnTable(
        [calculatedColumnsEnum.HOUR_PERIOD],
        null,
        columnTypeEnum.DIMENSION,
        true,
        null
      );

      this.measureColumnsTable1 = TableTools.Helper.constructColumnTable(
        [
          mesureEnum.PURCHASEHISTORY_AMOUNT,
          mesureEnum.SNACKHISTORY_AMOUNT_CB_TTC,
          mesureEnum.PURCHASEHISTORY_COUNTSOLDCARDS,
          mesureEnum.PURCHASEHISTORY_TOKEN,
          mesureEnum.PURCHASEHISTORY_BONUSTOKEN,
          calculatedColumnsEnum.STAFF_CREDITED_TOKENS,
        ],
        null,
        columnTypeEnum.MEASURE,
        true,
        null
      );

      this.groupHeadersTable1 = [
        { nbOfMeasures: 1, label: "" },
        { nbOfMeasures: 1, label: this.$t("group.revenueDirectPayment") },
        { nbOfMeasures: 1, label: "" },
        { nbOfMeasures: 3, label: this.$t("group.creditedTokens") },
      ];

      this.defaultSortedColumnTable1 = calculatedColumnsEnum.HOUR_PERIOD;
    },
    addCalculatedDataToDataTable1() {
      this.dataTable1 = this.dataTable1.map((data) => {
        const staffCardToken =
          data[mesureEnum.ADJUSTMENTHISTORY_TOKENCREDITED] || 0;
        const staffGameToken =
          data[mesureEnum.STAFFGAMEHISTORY_TOKENCREDITED] || 0;
        data[calculatedColumnsEnum.STAFF_CREDITED_TOKENS] =
          staffCardToken + staffGameToken;

        return data;
      });
    },
    async updateTable2(filters, dimensions) {
      try {
        const { signal } = this.resetAbortController("table2");
        this.dataReadyTable2 = false;
        var queries = [];

        queries.push({
          groupKey: groupKeyEnum.GROUP_GAMES,
          query: {
            measures: [
              mesureEnum.GAMEHISTORY_TOKEN,
              mesureEnum.GAMEHISTORY_TOKEN_PRICE,
            ],
            timeDimensions: this.timeDimension
              ? [{ ...this.timeDimension, dimension: dimensionEnum.GAMEHISTORY_DATETIME }]
              : [],
            dimensions: dimensions,
            filters: filters,
          },
        });

        var filtersSnack = [...filters];
        filtersSnack.push({
          dimension: dimensionEnum.SNACKPRODUCT_TYPE,
          operator: "equals",
          values: [snackProductTypeEnum.Snack.toString()],
        });
        queries.push({
          groupKey:
            groupKeyEnum.GROUP_SNACK + snackProductTypeEnum.Snack.toString(),
          query: {
            measures: [mesureEnum.SNACKHISTORY_TOKEN_PRICE],
            timeDimensions: this.timeDimension ? [this.timeDimension] : [],
            dimensions: dimensions,
            filters: filtersSnack,
          },
        });

        var resultSets = await TableTools.Helper.doMultipleRequest(queries, { signal });
        this.dataTable2 = TableTools.Helper.mergeResultsByCommonDimensions(
          resultSets,
          dimensions
        );
        this.addCalculatedDataToDataTable2();
        this.dataTable2 = this.completeAndCleanHours(
          this.dataTable2,
          this.measureColumnsTable2.map((col) => col.key)
        );

        // Totaux : attention on ne veut pas sommer les colonnes de pourcentage, mais plutôt calculer le pourcentage que représentent les montants totaux
        var totals = TableTools.Helper.calculateTotalsData(this.dataTable2, [
          TableTools.Helper.formatKeyWithGroupKey(
            calculatedColumnsEnum.REVENUE_EQUIVALENT_TTC,
            groupKeyEnum.GROUP_GAMES
          ),
          TableTools.Helper.formatKeyWithGroupKey(
            calculatedColumnsEnum.REVENUE_EQUIVALENT_TTC,
            groupKeyEnum.GROUP_SNACK + snackProductTypeEnum.Snack.toString()
          ),
          TableTools.Helper.formatKeyWithGroupKey(
            calculatedColumnsEnum.REVENUE_EQUIVALENT_TTC,
            groupKeyEnum.GROUP_SNACK +
            snackProductTypeEnum.Merchandising.toString()
          ),
        ]);
        this.totalsDataTable2 = this.calculateRevenuePercentage(totals);

        this.dataReadyTable2 = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        Tools.Alerts.Error(this.$swal, error);
      }
    },
    /** Initialise tout ce qui concerne l'affichage : quelles colonnes doivent réellement être affichées, dans quel ordre, dans quel groupe...
     * Attention : séparé ici car pour ce tableau tout est "figé", pas besoin de le refaire en fonction des valeurs choisies dans les filtres, mais ça n'est pas toujours le cas
     */
    initDisplayTable2() {
      this.dimensionColumnsTable2 = TableTools.Helper.constructColumnTable(
        [calculatedColumnsEnum.HOUR_PERIOD],
        null,
        columnTypeEnum.DIMENSION,
        true,
        null
      );
      const measuresGroup1 = TableTools.Helper.constructColumnTable(
        [
          calculatedColumnsEnum.REVENUE_EQUIVALENT_TTC,
          calculatedColumnsEnum.PERCENT_PER_ACTIVITY_GAME,
        ],
        groupKeyEnum.GROUP_GAMES,
        columnTypeEnum.MEASURE,
        true,
        null
      );
      const measuresGroup2 = TableTools.Helper.constructColumnTable(
        [
          calculatedColumnsEnum.REVENUE_EQUIVALENT_TTC,
          calculatedColumnsEnum.PERCENT_PER_ACTIVITY_SNACK,
        ],
        groupKeyEnum.GROUP_SNACK + snackProductTypeEnum.Snack.toString(),
        columnTypeEnum.MEASURE,
        true,
        null
      );

      this.measureColumnsTable2 = measuresGroup1
        .concat(measuresGroup2)
      this.groupHeadersTable2 = TableTools.Helper.constructGroupHeaders(
        [
          groupKeyEnum.GROUP_GAMES,
          groupKeyEnum.GROUP_SNACK + snackProductTypeEnum.Snack.toString()
        ],
        this.measureColumnsTable2
      );

      this.defaultSortedColumnTable2 = calculatedColumnsEnum.HOUR_PERIOD;
    },
    addCalculatedDataToDataTable2() {
      this.dataTable2 = this.dataTable2.map((data) => {
        const gameUsedTokensRevenue =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            mesureEnum.GAMEHISTORY_TOKEN_PRICE,
            groupKeyEnum.GROUP_GAMES
          )
          ] || 0;

        data[
          TableTools.Helper.formatKeyWithGroupKey(
            calculatedColumnsEnum.REVENUE_EQUIVALENT_TTC,
            groupKeyEnum.GROUP_GAMES
          )
        ] = gameUsedTokensRevenue;

        const snackUsedTokensRevenue =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            mesureEnum.SNACKHISTORY_TOKEN_PRICE,
            groupKeyEnum.GROUP_SNACK + snackProductTypeEnum.Snack.toString()
          )
          ] || 0;
        data[
          TableTools.Helper.formatKeyWithGroupKey(
            calculatedColumnsEnum.REVENUE_EQUIVALENT_TTC,
            groupKeyEnum.GROUP_SNACK + snackProductTypeEnum.Snack.toString()
          )
        ] = snackUsedTokensRevenue;

        return this.calculateRevenuePercentage(data);
      });
    },
    calculateRevenuePercentage(data) {
      const gameRevenue =
        data[
        TableTools.Helper.formatKeyWithGroupKey(
          calculatedColumnsEnum.REVENUE_EQUIVALENT_TTC,
          groupKeyEnum.GROUP_GAMES
        )
        ];

      const snackRevenue =
        data[
        TableTools.Helper.formatKeyWithGroupKey(
          calculatedColumnsEnum.REVENUE_EQUIVALENT_TTC,
          groupKeyEnum.GROUP_SNACK + snackProductTypeEnum.Snack.toString()
        )
        ];
      const totalRevenue =
        (gameRevenue || 0) + (snackRevenue || 0);

      data[
        TableTools.Helper.formatKeyWithGroupKey(
          calculatedColumnsEnum.PERCENT_PER_ACTIVITY_GAME,
          groupKeyEnum.GROUP_GAMES
        )
      ] = totalRevenue ? (gameRevenue * 100) / totalRevenue : 0;

      data[
        TableTools.Helper.formatKeyWithGroupKey(
          calculatedColumnsEnum.PERCENT_PER_ACTIVITY_SNACK,
          groupKeyEnum.GROUP_SNACK + snackProductTypeEnum.Snack.toString()
        )
      ] = totalRevenue ? (snackRevenue * 100) / totalRevenue : 0;

      return data;
    },
    completeAndCleanHours(datatable, shownColumnKeys) {
      if (datatable.length) {
        // Renommage de la colonne Hour pour faciliter les traitements personnalisés
        const hourKey = calculatedColumnsEnum.HOUR_PERIOD;
        var updatedDatatable = datatable.map((data) => {
          TableTools.Helper.renameKey(data, dimensionEnum.TIME_HOUR, hourKey);
          return data;
        });

        // Suppression des données vides en début et fin de période
        updatedDatatable = TableTools.Helper.sortDatatableByBusinessHour(
          datatable,
          hourKey
        );
        updatedDatatable = lodash.dropWhile(updatedDatatable, (data) => {
          return !shownColumnKeys.some((key) => data[key]);
        });
        updatedDatatable = lodash.dropRightWhile(updatedDatatable, (data) => {
          return !shownColumnKeys.some((key) => data[key]);
        });

        // Ajout de données pour les heures manquantes pendant la période
        if (updatedDatatable.length) {
          var missingHoursData = [];
          const startHourOrder = Tools.BusinessDateHelper.getOrderFromBusinessHour(
            updatedDatatable[0][hourKey]
          );
          const endHourOrder = Tools.BusinessDateHelper.getOrderFromBusinessHour(
            updatedDatatable[updatedDatatable.length - 1][hourKey]
          );

          let currentHourOrder = startHourOrder;
          while (currentHourOrder < endHourOrder) {
            const currentHour = Tools.BusinessDateHelper.getBusinessHourFromOrder(
              currentHourOrder
            );
            if (
              !updatedDatatable.some((data) => data[hourKey] === currentHour)
            ) {
              const dummyData = {};
              dummyData[hourKey] = currentHour;
              missingHoursData.push(dummyData);
            }
            currentHourOrder++;
          }
          return updatedDatatable.concat(missingHoursData);
        }
      }
      return [];
    },
    downloadPdf(data) {
      if (this.dataReadyTable1 && this.dataReadyTable2) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel(data) {
      if (this.dataReadyTable1 && this.dataReadyTable2) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv(data) {
      if (this.dataReadyTable1 && this.dataReadyTable2) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData(filename, subtitles) {
      const self = this;
      const renderer1 = this.$refs.tableRenderers1;
      const renderer2 = this.$refs.tableRenderers2;

      const sortKey1 = renderer1?.sortedColumn || self.defaultSortedColumnTable1;
      const sortKey2 = renderer2?.sortedColumn || self.defaultSortedColumnTable2;

      const data1 = TableTools.Helper.assembleExportDataTable(
        self.dataTable1,
        self.dimensionColumnsTable1,
        self.measureColumnsTable1,
        self.groupHeadersTable1,
        null,
        sortKey1,
        null,
        self.totalsDataTable1
      );
      const data2 = TableTools.Helper.assembleExportDataTable(
        self.dataTable2,
        self.dimensionColumnsTable2,
        self.measureColumnsTable2,
        self.groupHeadersTable2,
        null,
        sortKey2,
        null,
        self.totalsDataTable2
      );

      TableTools.Helper.storeExportData(
        [data1, data2],
        self.title,
        subtitles,
        filename
      );
    },
  },
};
</script>
